import React, {useCallback, useEffect, useMemo, useState} from "react"
import {
    Alert,
    Box,
    Button, Checkbox,
    ColumnLayout,
    Container,
    Form,
    FormField,
    Header,
    Link,
    SpaceBetween,
    Tiles
} from "@amzn/awsui-components-react-v3";
import * as Papa from "papaparse";
import {
    BULK_START_CSV_MAX_ROWS, BULK_START_PLUGIN_FIELD_NAME_DELIMITER, pleaseSelectLabel
} from "src/constants/workflow-instance";
import {BULK_START_ASYNC_ENABLED_PROGRAM_NAMES, BULK_START_ASYNC_RECOMMENDED_MAX_ROWS} from "src/constants/start-workflow-instance-async";
import {BulkStartRecipeExecutionsResult, Map} from "@amzn/sm-workflow-orchestration-service-js-client/lib/smworkfloworchestrationservicelambda";
import {toast} from "react-toastify";
import {SelectProps} from "@amzn/awsui-components-react-v3/polaris/select/interfaces";
import Select from "@amzn/awsui-components-react-v3/polaris/select";
import {WorkflowInstanceUtil} from "src/utils/workflow-instance-util";
import {CapabilityService} from "src/model/CapabilityService";
import {sortArrayAlphabetically} from "src/utils/array-helper";
import {AppRoutes} from "src/components/common/router-common";

export function BulkStartWorkflowInstanceV1() {
    const [selectedFile, setSelectedFile] = useState<null | Blob>();
    const [failedFileContent, setFailedFileContent] = useState<Array<Map>>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [totalNumberOfRows, setTotalNumberOfRows] = useState<number>(0)
    //Todo: abstarct  isContinuous state management to context API to avoid duplicate code in start & bulk start screen
    const [isContinuous, setIsContinuous] = useState(true)
    const [isContinuousCheckEnabled, setIsContinuousCheckEnabled] = useState(false)
    const [submissionMode, setSubmissionMode] = useState<'sync' | 'async'>('sync')
    const [asyncResult, setAsyncResult] = useState<BulkStartRecipeExecutionsResult>({
        successfulRequests: {},
        failedRequests: {}
    })
    const [capabilitySelectedOption, setCapabilitySelectedOption] = useState<SelectProps.Option>(pleaseSelectLabel);
    const [primitiveSelectedOption, setPrimitiveSelectedOption] = useState<SelectProps.Option>(pleaseSelectLabel);
    const [workflowDefinitionOption, setWorkflowDefinitionOption] = useState<SelectProps.Option>(pleaseSelectLabel);
    const [classificationTagSelectedOption, setClassificationTagSelectedOption] = useState<SelectProps.Option>(pleaseSelectLabel);

    const { data: capabilitiesData } = CapabilityService.useCapabilities();

    const capabilityOptions = useMemo(() => {
        if (!capabilitiesData) return [];
        const uniqueCapabilities = Array.from(new Set(capabilitiesData.map(cap => cap.capabilityId)));
        return sortArrayAlphabetically(uniqueCapabilities).map(cap => ({
            value: cap,
            label: cap
        }));
    }, [capabilitiesData]);

    const selectedCapability = useMemo(() => {
        if (!capabilitiesData || !capabilitySelectedOption?.value || !primitiveSelectedOption?.value) {
            return null;
        }
        return capabilitiesData.find(cap =>
            cap.capabilityId === capabilitySelectedOption.value &&
            cap.primitiveId === primitiveSelectedOption.value
        );
    }, [capabilitiesData, capabilitySelectedOption?.value, primitiveSelectedOption?.value]);

    const primitiveOptions = useMemo(() => {
        if (!capabilitiesData || !capabilitySelectedOption?.value) return [];

        const primitives = Array.from(
            new Set(
                capabilitiesData
                    .filter(cap => cap.capabilityId === capabilitySelectedOption.value)
                    .map(cap => cap.primitiveId)
            )
        );
        return sortArrayAlphabetically(primitives).map(prim => ({
            value: prim,
            label: prim
        }));
    }, [capabilitiesData, capabilitySelectedOption?.value]);

    const workflowDefinitionOptions = useMemo(() => {
        if (!selectedCapability) return [];

        const workflows = selectedCapability.workflows || [];
        const sortedWorkflows = sortArrayAlphabetically(workflows.map(w => w.recipeName));
        return sortedWorkflows.map(w => ({
            value: w,
            label: w
        }));
    }, [selectedCapability, capabilitySelectedOption.value, primitiveSelectedOption.value]);

    const selectedWorkflow = useMemo(() => {
        if (!selectedCapability || !workflowDefinitionOption?.value) return null;

        return selectedCapability.workflows?.find(w =>
            w.recipeName === workflowDefinitionOption.value
        );
    }, [selectedCapability, workflowDefinitionOption?.value]);

    const classificationTagOptions = useMemo(() => {
        if (!selectedWorkflow) return [];

        const classificationTags = selectedWorkflow?.allowedTags?.classificationTag || [];
        return sortArrayAlphabetically(classificationTags).map(tag => ({
            value: tag,
            label: tag
        }));
    }, [selectedWorkflow]);

    useEffect(() => {
        if (primitiveOptions.length === 1) {
            setPrimitiveSelectedOption(primitiveOptions[0]);
        }
    }, [primitiveOptions]);

    useEffect(() => {
        if (workflowDefinitionOptions.length === 1) {
            setWorkflowDefinitionOption(workflowDefinitionOptions[0]);
        }
    }, [workflowDefinitionOptions]);

    useEffect(() => {
        if (classificationTagOptions.length === 1) {
            setClassificationTagSelectedOption(classificationTagOptions[0]);
        }
    }, [classificationTagOptions]);

    const isAsyncEligible = BULK_START_ASYNC_ENABLED_PROGRAM_NAMES.includes(classificationTagSelectedOption.value || "");

    // Snap submissionMode back to 'sync' when the operator switches to a workflow
    // that isn't on the async allowlist, so a stale async selection can't drive a
    // submission against an unsupported recipe.
    useEffect(() => {
        if (!isAsyncEligible && submissionMode === 'async') {
            setSubmissionMode('sync');
        }
    }, [isAsyncEligible, submissionMode]);

    const handleCapabilityChange = useCallback((detail: SelectProps.ChangeDetail) => {
        setCapabilitySelectedOption(detail.selectedOption);
        setPrimitiveSelectedOption(pleaseSelectLabel);
        setWorkflowDefinitionOption(pleaseSelectLabel);
        setClassificationTagSelectedOption(pleaseSelectLabel);
    }, []);

    const handlePrimitiveChange = useCallback((detail: SelectProps.ChangeDetail) => {
        setPrimitiveSelectedOption(detail.selectedOption);
        setWorkflowDefinitionOption(pleaseSelectLabel);
        setClassificationTagSelectedOption(pleaseSelectLabel);
    }, []);

    const handleWorkflowDefinitionChange = useCallback((detail: SelectProps.ChangeDetail) => {
        setWorkflowDefinitionOption(detail.selectedOption);
        setClassificationTagSelectedOption(pleaseSelectLabel);
    }, []);



    function handleOnInput(e: any) {
        setSelectedFile(e.target.files[0])
    }

    /*
      If `true`, numeric and boolean data will be converted to their type instead of remaining strings.
      If user wants to set some columns to string, then can do it by appending ".string" in column header.
      For eg, if schema expects websiteId to be string, user can set column header like "crawler.websiteId.string".
     */

    function handleDynamicTyping(e: string | number): boolean {
        if (typeof e === "string") {
            const columnHeader = e.split(BULK_START_PLUGIN_FIELD_NAME_DELIMITER)
            return !((columnHeader.length > 2) && (columnHeader[2].toLowerCase() === "string"))
        }
        return true
    }

    function handleBulkStartButtonClick(e: any) {
        setFailedFileContent([])
        setAsyncResult({successfulRequests: {}, failedRequests: {}})
        if (capabilitySelectedOption.value === "") {
            toast.error("Please select a capability");
            return;
        }

        if (primitiveSelectedOption.value === "") {
            toast.error("Please select a primitive");
            return;
        }

        if (workflowDefinitionOption.value === "") {
            toast.error("Please select a workflow definition");
            return;
        }

        if (classificationTagSelectedOption.value === "") {
            toast.error("Please select a classification Tag")
            return;
        }

        if (selectedFile == null) {
            toast.error("Please select a file.")
            return;
        }

        if (submissionMode === 'async') {
            if (selectedFile.size === 0) {
                toast.error("CSV file is empty")
                return
            }
            setLoading(true)
            WorkflowInstanceUtil.submitAsyncBulkStartV1(
                selectedFile,
                classificationTagSelectedOption,
                workflowDefinitionOption,
                capabilitySelectedOption,
                primitiveSelectedOption,
                setAsyncResult,
                setLoading
            )
            return
        }

        Papa.parse(selectedFile, {
            download: true,
            header: true,
            dynamicTyping: handleDynamicTyping,
            skipEmptyLines: true,
            complete: (parsingResults) => {
                if (parsingResults.data.length === 0) {
                    toast.error("CSV File should have at least one row")
                    return
                }

                if (parsingResults.data.length >= BULK_START_CSV_MAX_ROWS) {
                    toast.error(`Max ${BULK_START_CSV_MAX_ROWS} rows allowed`)
                    return
                }
                setLoading(true)
                WorkflowInstanceUtil.handleParseCompleteV1(
                    parsingResults,
                    classificationTagSelectedOption,
                    workflowDefinitionOption,
                    capabilitySelectedOption,
                    primitiveSelectedOption,
                    setTotalNumberOfRows,
                    setFailedFileContent,
                    setLoading)
            },
            error: (error: Error) => {
                console.error("Parsing Error", JSON.stringify(error))
            }

        })
    }

    const bulkUploadSampleFileLinks = useMemo(() => {
        const limitText = submissionMode === 'sync'
            ? `Max ${BULK_START_CSV_MAX_ROWS} records.`
            : `Recommended: keep the file below ${BULK_START_ASYNC_RECOMMENDED_MAX_ROWS.toLocaleString()} rows.`;
        return (<>
            {limitText}
            <br/>
            <Link href={""} onFollow={() => WorkflowInstanceUtil.handleDownloadSampleLinkFollowV1()}>
                Download sample file for non-continuous monitoring
            </Link>
            <br/>
            <Link href={""} onFollow={() => WorkflowInstanceUtil.handleDownloadSampleLinkFollowV1(true)}>
                Download sample file for continuous monitoring
            </Link>
        </>)
    }, [submissionMode])

    return <Box padding={{top: "l"}}>
        <Container
            header={
                <Header
                    variant="h2"
                >
                    Bulk Start Workflow Instance
                </Header>
            }
        >
            <Alert
                id={"bulk-start-error-alert"}
                type={"error"} visible={submissionMode === 'sync' && failedFileContent.length > 0}> {failedFileContent.length} out
                of {totalNumberOfRows} records failed <Button
                    iconName={"download"}
                    onClick={() => WorkflowInstanceUtil.downloadBulkStartErrorFile(failedFileContent)}> Download Error
                    file </Button></Alert>

            {submissionMode === 'async' && Object.entries(asyncResult.successfulRequests).length > 0 && (
                <Alert
                    data-testid={"bulk-start-async-success-alert"}
                    type={"success"}
                    dismissible={true}
                    onDismiss={() => setAsyncResult(prev => ({...prev, successfulRequests: {}}))}>
                    <SpaceBetween direction="vertical" size="s">
                        {Object.entries(asyncResult.successfulRequests).map(([key, value]) => (
                            <div key={key}>
                                Successfully submitted bulk start request. View tracking workflow
                                <Link href={AppRoutes.executionDetail(value)}> {value} </Link>
                            </div>
                        ))}
                    </SpaceBetween>
                </Alert>
            )}

            {submissionMode === 'async' && asyncResult.failedRequests &&
                Object.keys(asyncResult.failedRequests).length > 0 && (
                <Alert
                    data-testid={"bulk-start-async-error-alert"}
                    type="error"
                    dismissible={true}
                    onDismiss={() => setAsyncResult(prev => ({...prev, failedRequests: {}}))}>
                    <SpaceBetween direction="vertical" size="s">
                        {Object.entries(asyncResult.failedRequests).map(([key, value]) => (
                            <p key={key}>{value}</p>
                        ))}
                    </SpaceBetween>
                </Alert>
            )}

            <Form
                id={"bulk-start-form"}
                actions={<Button
                    id={"bulk-start-submit-button"}
                    variant={"primary"}
                    loading={loading}
                    onClick={(e) => handleBulkStartButtonClick(e)}>Bulk Start Workflow Instance</Button>}>
                <ColumnLayout columns={1} borders={"vertical"}>
                    {/* Capability Selection */}
                    <FormField
                        id={"bulk-start-capability-selection-field"}
                        label="Select Capability"
                    >
                        <Select
                            id={"bulk-start-capability-selection-dropdown"}
                            selectedOption={capabilitySelectedOption}
                            disabled={loading}
                            onChange={({detail}) => handleCapabilityChange(detail)}
                            options={capabilityOptions}
                            selectedAriaLabel="Selected"
                            filteringType="auto"
                        />
                    </FormField>

                    {/* Primitive Selection */}
                    {capabilitySelectedOption?.value && (
                        <FormField
                            id={"bulk-start-primitive-selection-field"}
                            label="Select Primitive"
                        >
                            <Select
                                id={"bulk-start-primitive-selection-dropdown"}
                                selectedOption={primitiveSelectedOption}
                                disabled={loading}
                                onChange={({detail}) => handlePrimitiveChange(detail)}
                                options={primitiveOptions}
                                selectedAriaLabel="Selected"
                                filteringType="auto"
                            />
                        </FormField>
                    )}
                    {primitiveSelectedOption?.value && (
                        <FormField
                            id={"bulk-start-workflow-definition-field"}
                            label="Workflow Definition"
                        >
                            <Select
                                id={"bulk-start-workflow-definition-dropdown"}
                                selectedOption={workflowDefinitionOption}
                                disabled={loading}
                                onChange={({detail}) => handleWorkflowDefinitionChange(detail)}
                                options={workflowDefinitionOptions}
                                selectedAriaLabel="Selected"
                                filteringType="auto"
                            />
                        </FormField>
                    )}
                    {workflowDefinitionOption?.value && (
                        <Box padding={{top: "xs", bottom: "l"}}>
                            <FormField
                                id={"bulk-start-classification-tag-selection-field"}
                                label="Select Classification Tag"
                            >
                                <Select
                                    id={"bulk-start-classification-tag-selection-dropdown"}
                                    selectedOption={classificationTagSelectedOption}
                                    disabled={loading}
                                    onChange={({detail}) => {
                                        setClassificationTagSelectedOption(detail.selectedOption)
                                        setIsContinuous(true);
                                        setIsContinuousCheckEnabled(WorkflowInstanceUtil.isContinuousProgramVariantCheckEnabled(detail.selectedOption))
                                    }
                                    }
                                    options={classificationTagOptions}
                                    selectedAriaLabel="Selected"
                                    filteringType="auto"
                                />
                            </FormField>
                        </Box>
                    )}
                    {WorkflowInstanceUtil.isContinuousProgramVariantSupported(classificationTagSelectedOption.label) && <FormField
                        label={<>&nbsp;</>}>
                        <Checkbox
                            onChange={({detail}) => {
                                setIsContinuous(detail.checked);
                            } }
                            checked={isContinuous}
                            disabled={!isContinuousCheckEnabled}
                            data-testid={"continuous-wi-checkbox"}
                        >
                            Is Continuous Workflow
                        </Checkbox>
                    </FormField>}
                </ColumnLayout>
                {isAsyncEligible && (
                    <Box padding={{top: "xs", bottom: "l"}}>
                        <FormField
                            id={"bulk-start-submission-mode-field"}
                            label="Submission mode"
                            description={`Async submits to a traffic shaping system; recommended to keep files below ${BULK_START_ASYNC_RECOMMENDED_MAX_ROWS.toLocaleString()} rows. Sync calls the legacy synchronous API and is capped at 1000 rows.`}
                        >
                            <Tiles
                                data-testid="bulk-start-submission-mode-tiles"
                                value={submissionMode}
                                onChange={(e) => setSubmissionMode(e.detail.value as 'sync' | 'async')}
                                items={[
                                    {value: "sync", label: "Synchronous (legacy, ≤1000 rows)"},
                                    {value: "async", label: `Asynchronous (recommended ≤${BULK_START_ASYNC_RECOMMENDED_MAX_ROWS.toLocaleString()} rows)`}
                                ]}
                            />
                        </FormField>
                    </Box>
                )}
                <Box padding={{top: "xs", bottom: "l"}}>
                    <FormField label={"Select CSV file"}
                               id={"form-field-file-input"}
                               description={bulkUploadSampleFileLinks}>
                        <input data-testid={"bulk-start-file-input"} id={"bulk-start-file-input"} type={"file"}
                               onInput={handleOnInput}
                               onChange={handleOnInput}
                               accept=".csv" disabled={loading}/>
                    </FormField>
                </Box>
            </Form>

        </Container>

    </Box>
}
